import { connect, useDispatch } from 'react-redux';
import { FieldArray } from 'react-final-form-arrays';
import { Dispatch, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Field, FormSpy, useField } from 'react-final-form';
import cn from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { groupBy } from 'ramda';

import {
  getRequirementBlockId,
  REQUIREMENTS_BLOCK_ID,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmFormRef,
  getKnmErknmRequirementsSelectOptions,
  getKnmIsWatchOnly,
  getKnmDataCreated,
  getIsRsnCngPPXXII,
  getKnmIsErknmStatusApproving,
  getKnmIsTypeErknmPlanned,
  getKnmDataPlanId,
  getChangePlanReasonAfterConfirmEvent,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeCompleted,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { ErknmFillInProcessMessage } from 'KnmErknmPackage/MainModule/components/ErknmFillInProcessMessage';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ErknmRequirementsTable } from 'KnmErknmPackage/MainModule/components/ErknmRequirementsTable';
import { StructureRequirementsNpaModal } from 'KnmPackage/StructureRequirementsNpaModule/StructureRequirementsNpaModal';
import { IRequirementNpa } from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementNpa';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { ErknmApproveRemarkAddButton } from 'KnmErknmPackage/ErknmApproveRemarkModule/components/ErknmApproveRemarkAddButton';
import { ErknmApproveRemarkSectionCodeEnum } from 'KnmErknmPackage/ErknmApproveRemarkModule/enums/ErknmApproveRemarkSectionCodeEnum';
import { IKnmErknmFormReason } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormReason';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IKnmErknmFormViolatedRequirementsMandatory } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import {
  getFieldArrayErrorMessage,
  hasFieldArrayItemErrors,
} from 'CorePackage/MainModule/common/getFieldArrayErrorMessage';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { getUserRoleIsProsecutor } from 'AuthPackage/MainModule/selectors/authSelectors';
import {
  getLatestKnmErknmChangePlanReason,
  getRequirementsPlanChanges,
  getRequirementsNew,
  getKnmErknmIsPrescriptionOldRules,
} from 'KnmErknmPackage/MainModule/selectors/knmErknmSelectors';
import { IKnmErknmFormChangePlanReason } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormChangePlanReason';
import {
  getNpaListNewAction,
  setIsNpaStructureChanged,
} from 'KnmPackage/RequirementsNpaModalModule/actions/requirementsNpaActions';
import { IRequirementNpaResponse } from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementNpaResponse';
import {
  IRequirementsMandatoryNew,
  IStructuresUnitsAsTreeResponse,
} from 'APIPackage/RequirementsNpaModule/interfaces/IStructuresUnitsAsTreeResponse';
import {
  getIsNpaStructureChanged,
  getNpaListNew,
} from 'KnmPackage/RequirementsNpaModalModule/selectors/requirementsNpaSelectors';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { useLatestValue } from 'hooks/useLatestValue';
import { createTypedUseFormError } from 'hooks/useTypedFormField';
import { FieldWasChangedLabel } from 'KnmErknmPackage/MainModule/components/FieldWasChanged/FieldWasChangedLabel';
import { FieldWasChangedIcon } from 'KnmErknmPackage/MainModule/components/FieldWasChanged/FieldWasChangedIcon';
import { IKnmRequirementsNpa248 } from 'APIPackage/KnmModule/interfaces/IKnmRequirementsNpa248';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import styles from './ErknmRequirements.module.scss';
import { IKnmErknmFormRequirementNpa } from '../../MainModule/interfaces/IKnmErknmFormRequirementNpa';
import { IDictionaryKnoOrganizationSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IDictionarySupervisionTypeSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IDictionaryRequirementSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryRequirementSelectOption';
import { IKnmForm } from '../../../KnmPackage/MainModule/interfaces/IKnmForm';
import { NEW_REQUIREMENT_SELECT_OPTION_CODE } from '../../MainModule/constants/newRequirementSelectOption';
import { convertInitialKnmDateToFormDate } from '../../../KnmPackage/MainModule/common/convertInitialKnmDateToFormDate';
import { DATE_FORMAT_LONG_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';
import { ErknmFieldMark } from '../../MainModule/components/ErknmFieldMark';
import { ErknmApproveRemark } from '../../ErknmApproveRemarkModule/components/ErknmApproveRemark';
import { useIsErknmRequirementsEnabled } from '../../MainModule/fieldsAvailability/erknmRequirementsAvailability';
import { IRequirementsNpaInitValues } from '../../../KnmPackage/RequirementsNpaModalModule/components/RequirementsNpaModal';
import { IKnmPmFormRequirementsNpa } from '../../../KnmPmPackage/MainModule/interfaces/IKnmPmFormRequirementsNpa';
import { InspectionResultActionTypeEnum } from '../../../APIPackage/CatalogsModule/enums/InspectionResultActionTypeEnum';
import { InspectionResultFieldEnum } from '../../../APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import { ErknmInspectionResultMessage } from '../../MainModule/components/ErknmInspectionResultMessageMessage';
import collapseStyles from '../../../KnmPackage/MainModule/components/KnmCollapse.module.scss';
import { IRequirementStructuralUnit } from '../../../APIPackage/RequirementsNpaModule/interfaces/IRequirementStructuralUnit';
import { IRequirementsNpaItem } from '../../../KnmPackage/RequirementsNpaModalModule/interfaces/IRequirementsNpaItem';
import { IKnmErknmFormObject } from '../../MainModule/interfaces/IKnmErknmFormObject';
import { IKnmFormRiskIndicators } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRiskIndicators';
import {
  chekMandatoryRequirement,
  fetchMandatoryRequirementsListNew,
  fetchNpaFull,
  fetchStructuresUnitsAsTree,
  IChekMandatoryRequirementFilters,
  IFetchStructuresUnitsFilter,
  structuralUnitConverter,
} from '../../../APIPackage/RequirementsNpaModule/services/requirementsNpaApi';
import { coreSetLoading } from '../../../CorePackage/MainModule/actions/coreActions';
import { useDebouncedValue } from '../../../hooks/useDebouncedMemo';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';
import { arraysAreEqual } from '../../../functions/arraysAreEqual';
import { convertFormDateValueToKnmDataDate } from '../../../KnmPackage/MainModule/common/convertFormDateValueToKnmDataDate';
import { Hint } from '../../../CorePackage/MainModule/components/Hint';
import { useLinkedPassportValidation } from '../../MainModule/hooks/useLinkedPassportValidation';

interface IErknmRequirementsProps {
  knoOrganization?: IDictionaryKnoOrganizationSelectOption;
  kindControl?: IDictionarySupervisionTypeSelectOption;
  requirementsNpa?: IKnmErknmFormRequirementNpa[];
  reasonFieldValue: IKnmErknmFormReason | undefined;
  objects?: IKnmErknmFormObject[];
  riskIndicators?: IKnmFormRiskIndicators[];
}

interface IErknmRequirementsStoreProps {
  formRef: IKnmFormRef | undefined;
  isWatchOnly: boolean;
  basicRequirementsSelectOptions: IDictionaryRequirementSelectOption[] | undefined;
  created: string | null | undefined;
  settings: IAdminSetting[] | undefined;
  isNpaStructureChanged: boolean | null;
  npaListNew: IRequirementNpaResponse | null;
  isRsnCngPPXXII: boolean;
  isProsecutor: boolean | undefined;
  lastChangePlanReason?: IKnmErknmFormChangePlanReason;
  isKnmStatusApproving?: boolean;
  isKnmStatusTypeCarriedOut: boolean;
  isKnmStatusTypeCompleted: boolean;
  requirementsPlanChanges?: IKnmRequirementsNpa248[];
  requirementsNew?: IKnmRequirementsNpa248[];
  isPlanned: boolean;
  changePlanReasonAfterConfirm?: IKnmErknmFormChangePlanReason[];
  isPassportByCheckPrescriptionOldRules: boolean | undefined;
}

const useError = createTypedUseFormError<IKnmForm>();

const ErknmRequirementsWithoutStore = ({
  isWatchOnly,
  formRef,
  knoOrganization,
  kindControl,
  requirementsNpa,
  basicRequirementsSelectOptions,
  created,
  settings,
  reasonFieldValue,
  objects = [],
  riskIndicators = [],
  isRsnCngPPXXII,
  isProsecutor,
  lastChangePlanReason,
  isNpaStructureChanged,
  npaListNew,
  isKnmStatusApproving,
  isKnmStatusTypeCarriedOut,
  isKnmStatusTypeCompleted,
  requirementsPlanChanges,
  requirementsNew,
  isPlanned,
  changePlanReasonAfterConfirm,
  isPassportByCheckPrescriptionOldRules,
}: IErknmRequirementsProps & IErknmRequirementsStoreProps) => {
  const isEnabled = useIsErknmRequirementsEnabled();
  const isAddingDeletingEnabled = isEnabled && !!knoOrganization && !!kindControl;
  const requirementsNpaFieldName = 'requirementsNpaErknm' as keyof IKnmForm;
  const knoVersionId = knoOrganization?.value.dictVersionId || '';
  const supervisionVersionId = kindControl?.value.dictVersionId || '';
  const withChangePlanReason = lastChangePlanReason?.reasonChangeType?.code === 'RSN_CHNG_PP_XXII';
  const isImpossibilityOfPrevious = reasonFieldValue?.type?.name === KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS;
  const isPrescriptionCheckReason = reasonFieldValue?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK;
  const linkPassportId = reasonFieldValue?.links?.find(e => e.id)?.id;
  const isImpossibilityOfPreviousRef = useLatestValue(isImpossibilityOfPrevious);
  // Данные параметры используются для фильтра списка ОТ при их добавлении,
  // при изменении этих фильтров нам нужно убедиться, что уже выбранные ОТ все еще им соответствуют или выдать ошибку валидации
  const [currentRequirementsFilters, setCurrentRequirementsFilters] = useState<IChekMandatoryRequirementFilters>({
    objectTypeId: undefined,
    objectKindId: undefined,
    objectSubkindId: undefined,
    riskIndicatorId: undefined,
  });

  const [invalidMandatoryIds, setInvalidMandatoryIds] = useState<number[]>([]);

  const isRsnChngPpXxii = changePlanReasonAfterConfirm
    ? changePlanReasonAfterConfirm.some(item => item?.reasonChangeType?.code === 'RSN_CHNG_PP_XXII')
    : false;

  const isFirstLoad = useRef<boolean>(true);

  const hasChangesInRequirements = useMemo(() => {
    if (!requirementsNew || !requirementsPlanChanges) {
      return false;
    }

    if (requirementsNew.length !== requirementsPlanChanges.length) {
      return true;
    }

    const sortedCurrent = [...requirementsNew].sort((a, b) => (a.guid || '').localeCompare(b.guid || ''));
    const sortedChanges = [...requirementsPlanChanges].sort((a, b) => (a.guid || '').localeCompare(b.guid || ''));

    return sortedCurrent.some((req, index) => {
      const changeReq = sortedChanges[index];

      return req.guid !== changeReq?.guid;
    });
  }, [formRef, requirementsPlanChanges]);

  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const update = formRef?.current.mutators.update;
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const { error } = useError(requirementsNpaFieldName);
  const fieldError = getFieldArrayErrorMessage(error);

  const isDisabled = isWatchOnly || !knoOrganization || !kindControl;

  const requirementsSelectOptions: IDictionaryRequirementSelectOption[] | undefined = useMemo(() => {
    return basicRequirementsSelectOptions || [];
  }, [basicRequirementsSelectOptions]);

  const isEnablePaperlessDocument = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  // Настройка включающая дополнительные фильтры в рамках 4.1.24
  const isLaterThanNpaValidationSetting: boolean =
    !!created &&
    isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.NPA_VALIDATION_ERVK_ROT_4_1_24_STARTS_FROM);

  // Настройка включающая валидацию заполнения НПА для КНМ
  const isLaterThanNpaValidationKnmSetting: boolean =
    !!created &&
    isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.NPA_VALIDATION_ERVK_ROT_4_1_24_KNM_STARTS_FROM);

  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );

  // Если паспорт имеет ссылку на исходный, то нужно проверить его основание для отображения валидации ФОТ + КНО + ВК
  const isNeedDisableNpaValidation = useLinkedPassportValidation({
    linkPassportId,
    settings,
    isPrescriptionCheckReason,
    isImpossibilityOfPrevious,
    isEnablePrescriptionCheck,
  });
  const isNeedDisableNpaValidationRef = useLatestValue(isNeedDisableNpaValidation);

  // @ts-ignore
  const requirementsNpaValues: IKnmErknmFormRequirementNpa[] | undefined = formRef?.current.getFieldState(
    requirementsNpaFieldName,
  )?.value;

  const npaUsedIds = useMemo(() => {
    const arr = [] as number[];

    requirementsNpaValues?.forEach((el: IKnmErknmFormRequirementNpa) => {
      el.mandatory?.forEach((item: IKnmErknmFormViolatedRequirementsMandatory) => {
        if (item.mandatory?.id) {
          arr.push(item.mandatory?.id);
        }
      });
    });

    return arr;
  }, [requirementsNpaValues]);

  const structuresUnitsFilters: IFetchStructuresUnitsFilter = useMemo(() => {
    const objectTypeIds = objects
      ?.map(e => e.objectType?.value?.dictVersionId)
      ?.filter((e): e is string => typeof e === 'string');

    const objectKindIds = objects
      ?.map(e => e.objectKind?.value?.dictVersionId)
      ?.filter((e): e is string => typeof e === 'string');

    const objectSubkindIds = objects
      ?.map(e => e.objectSubKind?.value?.dictVersionId)
      ?.filter((e): e is string => typeof e === 'string');

    const riskIndicatorIds = riskIndicators
      ?.map(e => e.riskIndicator?.value?.dictVersionId)
      ?.filter((e): e is string => typeof e === 'string');

    return {
      objectTypeIds,
      objectKindIds,
      objectSubkindIds,
      riskIndicatorIds,
    };
  }, [objects, riskIndicators]);

  // Оптимизация для серверной валидации выбранных ОТ после установки значений фильтров
  const structuresUnitsFiltersOptimize = useDebouncedValue(structuresUnitsFilters, 1000);

  // Наблюдаем за изменениями фильтров для выбора ОТ
  useEffect(() => {
    if (!isLaterThanNpaValidationSetting) return;
    // Ждём, пока определится флаг отключения валидации КНО/ВК, иначе валидация отработает по устаревшему значению
    if (isNeedDisableNpaValidation === undefined) return;

    const { objectSubkindId, objectKindId, objectTypeId, riskIndicatorId } = currentRequirementsFilters;

    const requirements = formRef?.current.getState().values.requirementsNpaErknm as
      | IKnmErknmFormRequirementNpa[]
      | undefined;

    const newFilters = { ...currentRequirementsFilters };
    let hasFilterChange = false;
    let isControlObjectChanged = false;
    let isRiscIndicatorsChanged = false;

    const objectSubkindIdFilter =
      Array.isArray(structuresUnitsFilters.objectSubkindIds) && structuresUnitsFilters.objectSubkindIds.length
        ? structuresUnitsFilters.objectSubkindIds[0]
        : undefined;

    if (objectSubkindId !== objectSubkindIdFilter) {
      newFilters.objectSubkindId = objectSubkindIdFilter;
      hasFilterChange = true;
      isControlObjectChanged = true;
    }

    const objectKindIdFilter =
      Array.isArray(structuresUnitsFilters.objectKindIds) && structuresUnitsFilters.objectKindIds.length
        ? structuresUnitsFilters.objectKindIds[0]
        : undefined;

    if (objectKindId !== objectKindIdFilter) {
      newFilters.objectKindId = objectKindIdFilter;
      hasFilterChange = true;
      isControlObjectChanged = true;
    }

    const objectTypeIdFilter =
      Array.isArray(structuresUnitsFilters.objectTypeIds) && structuresUnitsFilters.objectTypeIds.length
        ? structuresUnitsFilters.objectTypeIds[0]
        : undefined;

    if (objectTypeId !== objectTypeIdFilter) {
      newFilters.objectTypeId = objectTypeIdFilter;
      hasFilterChange = true;
      isControlObjectChanged = true;
    }

    const riskIndicatorIdFilter =
      Array.isArray(structuresUnitsFilters.riskIndicatorIds) && structuresUnitsFilters.riskIndicatorIds.length
        ? structuresUnitsFilters.riskIndicatorIds
        : undefined;

    if (!arraysAreEqual(riskIndicatorId, riskIndicatorIdFilter)) {
      hasFilterChange = true;
      isRiscIndicatorsChanged = true;
      newFilters.riskIndicatorId = riskIndicatorIdFilter;
    }

    setCurrentRequirementsFilters(newFilters);

    // Если какие-то из фильтров поменялись, то запускаем бек-енд валидацию соответствия добавленных ОТ измененным фильтрам
    if (hasFilterChange || isControlObjectChanged || isRiscIndicatorsChanged) {
      if (Array.isArray(requirements) && requirements.length > 0) {
        const npaRequest: IChekMandatoryRequirementFilters['npaRequest'] = [];

        requirements.forEach(requirement => {
          requirement?.mandatory?.forEach(item => npaRequest.push({ mrId: String(item.mandatory.id) }));
        });
        try {
          chekMandatoryRequirement({
            supervisionVersionId,
            knoVersionId,
            ...newFilters,
            npaRequest,
          })
            .then(resp => {
              const validationMap = new Map(
                resp.mandatoryRequirements.map(i => [i.mandatoryRequirementId, i] as const),
              );
              let hasChanges = false;

              const nextRequirements = requirements.map(requirement => ({
                ...requirement,
                mandatory: requirement.mandatory.map(m => {
                  const validationResult = validationMap.get(m.mandatory.id);
                  // Проставление флагов валидности
                  const hasInvalidControlObjectFilter =
                    validationResult?.objectKindIsCorrect === false ||
                    validationResult?.objectTypeIsCorrect === false ||
                    validationResult?.objectSubkindIsCorrect === false;
                  const hasInvalidRiscIndicatorsFilter = validationResult?.riskIndicatorIsCorrect === false;
                  const hasInvalidKno =
                    !isNeedDisableNpaValidationRef.current && validationResult?.knoVersionIsCorrect === false;
                  const hasInvalidSupervision =
                    !isNeedDisableNpaValidationRef.current && validationResult?.supervisionIsCorrect === false;

                  if (
                    m.hasInvalidControlObjectFilter !== hasInvalidControlObjectFilter ||
                    m.hasInvalidRiscIndicatorsFilter !== hasInvalidRiscIndicatorsFilter ||
                    m.hasInvalidKno !== hasInvalidKno ||
                    m.hasInvalidSupervision !== hasInvalidSupervision
                  ) {
                    hasChanges = true;
                  }

                  return {
                    ...m,
                    hasInvalidControlObjectFilter,
                    hasInvalidRiscIndicatorsFilter,
                    hasInvalidKno,
                    hasInvalidSupervision,
                  };
                }),
              }));

              if (hasChanges) {
                formRef?.current.change(requirementsNpaFieldName, nextRequirements);
              }
            })
            .finally(() => {
              /**
               * После update полей hasInvalidControlObjectFilter и hasInvalidRiscIndicatorsFilter
               * в форме встречаются места, где происходит реакция на изменения (состояния dirty как всей формы, так и
               * непосредственно этих полей + сравнения в FormLeavingGuard). В finally, после загрузки паспорта и
               * инициализации этих полей сбрасываем initialState формы для исключения реакции на указанные выше поля.
               */
              if (isFirstLoad.current && formRef?.current) {
                const currentValues = formRef.current.getState().values;

                formRef.current.reset(currentValues);

                isFirstLoad.current = false;
              }
            });
        } catch (e) {
          console.error(e);
        }
      }
    }
  }, [structuresUnitsFiltersOptimize, isLaterThanNpaValidationSetting, isNeedDisableNpaValidation]);

  const [initValues, setInitValues] = useState<IRequirementsNpaInitValues | undefined>(undefined);
  const [isRequirementsModalVisible, setIsRequirementsModalVisible] = useState(false);
  const isRequirementDeleted = useField<boolean>('isRequirementDeleted').input.value ?? false;

  useEffect(() => {
    if (!isNpaStructureChanged || !knoVersionId || !supervisionVersionId) return;

    dispatch(getNpaListNewAction({ knoVersionId, supervisionVersionId }));
    dispatch(setIsNpaStructureChanged(false));
  }, [isNpaStructureChanged, knoVersionId, supervisionVersionId, dispatch]);

  // TODO: to transfer the validation logic after making changes to the knm in validateInvalidFields
  useEffect(() => {
    if (isKnmStatusTypeCarriedOut || isKnmStatusTypeCompleted) return;
    if (isNeedDisableNpaValidation === undefined || isNeedDisableNpaValidation) return;
    if (!isPlanned || !isRsnChngPpXxii) return;
    if (!npaListNew || !formRef?.current || !update) return;

    const requirements = formRef.current.getState().values.requirementsNpaErknm as
      | IKnmErknmFormRequirementNpa[]
      | undefined;

    if (!requirements?.length) return;

    const validNpaIds = npaListNew ? new Set(npaListNew.requirements.map(req => req.id)) : new Set<number>();

    const shouldCheckStructure = knoVersionId && supervisionVersionId;

    const runValidation = async () => {
      const npaGroups = groupBy((req: IKnmErknmFormRequirementNpa) => req.npa.id.toString(), requirements);

      const validationPromises = Object.entries(npaGroups).map(async ([npaIdStr, group]) => {
        const npaId = Number(npaIdStr);

        let validUnitIds: Set<string> = new Set();

        if (shouldCheckStructure) {
          try {
            const units = await fetchStructuresUnitsAsTree({
              npaId,
              knoVersionId,
              supervisionVersionId,
              knmCreationDate: convertFormDateValueToKnmDataDate(created) || undefined,
              withRequirements: false,
              suId: initValues?.structuralUnit?.id?.toString(),
              mrIds: undefined,
              objectTypeIds: undefined,
              ...structuresUnitsFilters,
            });

            const extractIds = (nodes: IStructuresUnitsAsTreeResponse['nodes']): Set<string> => {
              const ids = new Set<string>();
              const traverse = (list: IStructuresUnitsAsTreeResponse['nodes']) => {
                list.forEach(node => {
                  if (node.id) ids.add(node.id);
                  if (node.children?.length) traverse(node.children);
                });
              };

              traverse(nodes);

              return ids;
            };

            validUnitIds = extractIds(units.nodes);
          } catch (e) {
            validUnitIds = new Set();
          }
        }

        const itemUpdatePromises = group.map(async item => {
          const index = requirements.findIndex(r => r.guid === item.guid);

          if (index === -1) return;

          const hasInvalidExistNpa = validNpaIds.size === 0 ? false : !validNpaIds.has(item.npa.id);

          const hasInvalidStructureNpa = shouldCheckStructure
            ? !validUnitIds.has(String(item.structuralUnit.id))
            : false;

          let invalidMandatoryNpaIds: number[] = [];
          let hasInvalidMandatoryNpa = false;

          if (!hasInvalidExistNpa && !hasInvalidStructureNpa) {
            const unitId = item.structuralUnit.id;

            if (unitId) {
              try {
                const mandatoryData = await fetchMandatoryRequirementsListNew({
                  npaId,
                  suId: String(unitId),
                  knoVersionId,
                  supervisionVersionId,
                  knmCreationDate: convertFormDateValueToKnmDataDate(created) || undefined,
                });
                const serverIds = new Set(
                  Array.isArray(mandatoryData) ? mandatoryData.map((m: IRequirementsMandatoryNew) => m.id) : [],
                );

                const requiredIds = Array.isArray(item.mandatory)
                  ? item.mandatory.filter(m => m?.mandatory?.id).map(m => m.mandatory.id)
                  : [];

                if (requiredIds.length > 0) {
                  invalidMandatoryNpaIds = requiredIds.filter(reqId => !serverIds.has(reqId));
                  hasInvalidMandatoryNpa = invalidMandatoryNpaIds.length > 0;
                }
              } catch (e) {
                dispatch(
                  addNoticeAction({
                    renderContent: 'Не удалось провести проверку ФОТ по связке КНО + ВК ',
                    noticeType: NoticeTypesEnum.ERROR,
                    messageType: MessageTypesEnum.INTERNAL_SERVER,
                  }),
                );
              }
            }
          }

          const formNpa: IKnmErknmFormRequirementNpa = {
            ...item,
            hasInvalidExistNpa,
            hasInvalidStructureNpa,
            hasInvalidMandatoryNpa,
          };

          // ToDo: надо эту валидацию вынести в стор, по аналогии с валидацией ОБК + Индикаторы риска
          setInvalidMandatoryIds(prevState => [...prevState, ...invalidMandatoryNpaIds]);

          update(requirementsNpaFieldName, index, formNpa);
        });

        await Promise.all(itemUpdatePromises);
      });

      await Promise.all(validationPromises);
    };

    setInvalidMandatoryIds([]);
    runValidation();
  }, [
    npaListNew,
    formRef,
    isNpaStructureChanged,
    knoVersionId,
    supervisionVersionId,
    structuresUnitsFilters,
    initValues?.structuralUnit?.id,
    isKnmStatusTypeCarriedOut,
    isKnmStatusTypeCompleted,
    isNeedDisableNpaValidation,
  ]);

  const handleAddRequirementsNpa = (npaItems: IRequirementsNpaItem[]) => {
    const requirementsNpaErknm = formRef?.current?.getState()?.values?.requirementsNpaErknm ?? [];

    push &&
      update &&
      npaItems.forEach(npa => {
        const existedNpaWithSUIndex = requirementsNpaErknm?.findIndex(
          item => item.npa.id === npa.npa.id && item.structuralUnit.id === npa.structuralUnit.id,
        );

        // Если есть НПА с нужной СЕ, добавляем формулировку в его список
        if (existedNpaWithSUIndex > -1) {
          const mandatory =
            npa.mandatory?.map(mandatoryItem => ({
              mandatory: mandatoryItem,
            })) ?? [];

          update(requirementsNpaFieldName, existedNpaWithSUIndex, {
            ...requirementsNpaErknm[existedNpaWithSUIndex],
            mandatory,
          });
        } else {
          const existedNpaIndex = requirementsNpaErknm?.findIndex(
            item =>
              item.npa.id === npa.npa.id &&
              item.structuralUnit.id !== npa.structuralUnit.id &&
              !item?.mandatory?.length,
          );
          const newNPA = {
            ...npa,
            isNew: true,
            mandatory: npa.mandatory?.map(mand => ({
              mandatory: mand,
            })),
          };

          // Если есть НПА, внутри формулировки отсутствуют и СЕ не соответствует СЕ из добавляемой формулировки - обновляем НПА (новый СЕ + формулировка)
          if (existedNpaIndex > -1) {
            update(requirementsNpaFieldName, existedNpaIndex, newNPA);
          } else {
            // Иначе просто добавляем новый НПА
            push(requirementsNpaFieldName, newNPA);
          }
        }
      });
  };

  const handleAddMandatory = (npa: IRequirementNpa, structuralUnit: IRequirementStructuralUnit) => {
    setInitValues({
      npa,
      structuralUnit,
    });
    setIsRequirementsModalVisible(true);
  };

  const makeTitleSelectChangeHandler = useCallback(
    (fieldName: string) => (value: IDictionaryRequirementSelectOption | undefined) => {
      const titleFieldName = `${fieldName}.manualTitle` as keyof IKnmForm;
      const nameNpaFieldName = `${fieldName}.manualNameNpa` as keyof IKnmForm;
      const dateNpaFieldName = `${fieldName}.manualDateNpa` as keyof IKnmForm;

      if (!value || value?.code === NEW_REQUIREMENT_SELECT_OPTION_CODE) {
        formRef?.current.change(titleFieldName);
        formRef?.current.change(nameNpaFieldName);
        formRef?.current.change(dateNpaFieldName);
      } else {
        formRef?.current.change(titleFieldName, value.title);
        formRef?.current.change(nameNpaFieldName, value.nameNpa);
        const dateNpa = value.numberNpa
          ? convertInitialKnmDateToFormDate(value.numberNpa, DATE_FORMAT_LONG_FNS)
          : undefined;

        formRef?.current.change(dateNpaFieldName, dateNpa);
      }
    },
    [formRef],
  );

  const addField = () => {
    if (isAddingDeletingEnabled) {
      setInitValues(undefined);
      setIsRequirementsModalVisible(true);
    }
  };

  const addAllFields = async () => {
    if (isAddingDeletingEnabled) {
      dispatch(coreSetLoading(true));
      try {
        // Запрос списка всех ОТ с фильтрами
        const data = await fetchNpaFull({
          ...structuresUnitsFilters,
          knoVersionId: knoOrganization?.value.dictVersionId || '',
          supervisionVersionId: kindControl?.value.dictVersionId || '',
        });

        const result: IRequirementsNpaItem[] = [];

        data.forEach(item => {
          item?.structuralUnits?.forEach(su => {
            const resultObj: IRequirementsNpaItem = {
              guid: uuidv4(),
              npa: item.npa,
              structuralUnit: structuralUnitConverter(su.structuralUnit),
              mandatory: su.mandatoryRequirements,
            };

            result.push(resultObj);
          });
        });

        formRef?.current.change('requirementsNpaErknm');
        handleAddRequirementsNpa(result);
        setIsRequirementsModalVisible(false);
      } catch (e) {
        dispatch(
          addNoticeAction({
            renderContent: 'Не удалось добавить все ОТ по индикатору',
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.INTERNAL_SERVER,
          }),
        );
      } finally {
        dispatch(coreSetLoading(false));
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const validate = (value: any[]) => {
    if (isImpossibilityOfPreviousRef.current) {
      return makeRequired('Поле не может быть пустым')(value);
    }

    return '';
  };

  return (
    <>
      <FieldBlock
        label={
          <>
            Обязательные требования, подлежащие проверке
            <ErknmFieldMark fieldName={requirementsNpaFieldName} />
          </>
        }
        id={REQUIREMENTS_BLOCK_ID}
        button={
          <>
            {!isImpossibilityOfPrevious && (
              <Button
                className="shared-btn-add"
                text="Добавить"
                type="button"
                variant="link"
                color="primary"
                id="erknmRequirementsNpaAddButton"
                disabled={
                  isRsnCngPPXXII
                    ? false
                    : isDisabled ||
                      !isAddingDeletingEnabled ||
                      (isEnablePaperlessDocument &&
                        !!reasonFieldValue?.links?.length &&
                        isPassportByCheckPrescriptionOldRules === false)
                }
                onClick={addField}
              />
            )}
            {reasonFieldValue?.type?.indicatorRiskRequired && (
              <Button
                className="shared-btn-add"
                text="Добавить все ОТ по индикатору"
                type="button"
                variant="link"
                color="primary"
                id="erknmRequirementsNpaAddAllButton"
                disabled={isDisabled || !isAddingDeletingEnabled || !riskIndicators?.length}
                onClick={addAllFields}
              />
            )}
            <ErknmApproveRemarkAddButton sectionCode={ErknmApproveRemarkSectionCodeEnum.REQUIREMENTS} />
          </>
        }
        fillInProcess={<ErknmFillInProcessMessage name="requirementsErknm" />}
      >
        <Hint
          text={`Раздел зависит от: “Контрольный (надзорный) орган”, “Вид контроля (надзора)”, “Сведения об объектах контроля”${
            reasonFieldValue?.type?.name === KnmReasonTypeNameEnum.RISK_INDICATORS ? ', “Индикатор риска”' : ''
          }`}
        />
        {isProsecutor && isKnmStatusApproving && withChangePlanReason && hasChangesInRequirements && (
          <FieldWasChangedLabel
            text="Внесены изменения в КНМ в связи с дополнением соответствующих нормативных правовых актов новыми
              обязательными требованиями"
          />
        )}
        <ErknmApproveRemark sectionCode={ErknmApproveRemarkSectionCodeEnum.REQUIREMENTS} />
        {!!fieldError && <p className="shared-input-error shared-row__small-margin">{fieldError}</p>}

        <FormSpy subscription={{ errors: true, touched: true, values: true }}>
          {props => (
            <div className={styles.ErknmRequirementsNpaWrapper}>
              <FieldArray name={requirementsNpaFieldName} validate={validate}>
                {({ fields }) =>
                  fields.map((name, index) => {
                    const fieldValue: IKnmPmFormRequirementsNpa = fields.value?.[index];
                    const {
                      npa,
                      structuralUnit,
                      hasInvalidExistNpa,
                      hasInvalidStructureNpa,
                      mandatory: mandatoryValue,
                    } = fieldValue;

                    if (!npa || !structuralUnit) return null;
                    const isFieldTouched = props.touched?.requirementsNpaErknm;
                    const npaErrorField = props.errors?.requirementsNpaErknm?.[index];
                    const npaError = npaErrorField?.npa;
                    const mandatoryError = getFieldArrayErrorMessage(npaErrorField?.mandatory);
                    const isMandatoryItemError = hasFieldArrayItemErrors(npaErrorField?.mandatory);

                    const inspectionRed = fieldValue.inspectionResults?.find(
                      i =>
                        i.resultActionType === InspectionResultActionTypeEnum.LIGHT_RED &&
                        (i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_REQUIREMENTS_NPA ||
                          i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_STRUCTURAL_UNIT),
                    );
                    const inspectionYellow = fieldValue.inspectionResults?.find(
                      i =>
                        i.resultActionType === InspectionResultActionTypeEnum.LIGHT_YELLOW &&
                        (i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_REQUIREMENTS_NPA ||
                          i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_STRUCTURAL_UNIT),
                    );
                    let inspectionMessage = '';

                    if (fieldValue.inspectionResults) {
                      inspectionMessage = fieldValue.inspectionResults
                        ?.filter(
                          i =>
                            i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_REQUIREMENTS_NPA ||
                            i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_STRUCTURAL_UNIT,
                        )
                        .map(i => i.inspectionMessage)
                        .join(', ');
                    }
                    const withIconChange =
                      isProsecutor &&
                      isKnmStatusApproving &&
                      !requirementsPlanChanges?.some(it => it.guid === fieldValue.guid);

                    const hasInvalidRiscIndicatorsFilter = !!fieldValue.mandatory?.find(
                      m => m.hasInvalidRiscIndicatorsFilter,
                    );
                    const hasInvalidControlObjectFilter = !!fieldValue.mandatory?.find(
                      m => m.hasInvalidControlObjectFilter,
                    );
                    const hasInvalidKnoSupervisionFilter = !!fieldValue.mandatory?.find(
                      m => m.hasInvalidKno || m.hasInvalidSupervision,
                    );

                    return (
                      <div key={name}>
                        <div
                          className={cn(styles.RequirementsNpaItem, {
                            [styles.RequirementsNpaItemError]: npaError || mandatoryError || isMandatoryItemError,
                          })}
                        >
                          <KnmCollapse
                            theme="fields"
                            headerClassName={cn([
                              {
                                [styles.RequirementsNpaItemBlockInputInspectionResultYellow]: inspectionYellow,
                                [styles.RequirementsNpaItemBlockInputInspectionResultRed]:
                                  inspectionRed ||
                                  (isLaterThanNpaValidationKnmSetting &&
                                    (hasInvalidRiscIndicatorsFilter ||
                                      hasInvalidControlObjectFilter ||
                                      hasInvalidKnoSupervisionFilter)),
                              },
                            ])}
                            id={`requirements[${index}]`}
                            title={
                              <div className={styles.CollapseTitle}>
                                <div>
                                  <span className={styles.CollapseTitleGrey}>НПА №</span>
                                  {npa.number}
                                  <span className={styles.CollapseTitleGrey}>от</span>
                                  {npa.acceptanceDate}
                                </div>
                                {(inspectionRed || inspectionYellow) && (
                                  <ErknmInspectionResultMessage
                                    inspectionResult={{
                                      inspectedField: InspectionResultFieldEnum.ROT_REGISTRY_REQUIREMENTS_NPA,
                                      inspectionMessage,
                                      resultActionType: inspectionRed
                                        ? InspectionResultActionTypeEnum.LIGHT_RED
                                        : InspectionResultActionTypeEnum.LIGHT_YELLOW,
                                    }}
                                  />
                                )}
                                {withIconChange && <FieldWasChangedIcon text="Добавлена новая запись" />}
                              </div>
                            }
                            onClose={
                              (isWatchOnly || !isAddingDeletingEnabled) && !isRsnCngPPXXII
                                ? undefined
                                : () => {
                                    return (
                                      remove &&
                                      remove(requirementsNpaFieldName, index) &&
                                      formRef?.current?.change('isRequirementDeleted', true)
                                    );
                                  }
                            }
                            withIconChange={withIconChange}
                          >
                            <>
                              <Field name={`${name}.npa`}>{() => null}</Field>
                              <div className={styles.NpaName}>{npa.title}</div>
                              <div className={styles.StructureUnitTitle}>
                                <span>Структурная единица</span>
                                <span>{structuralUnit.title}</span>
                              </div>
                              <div className={styles.BeforeMandatoryBlock}>
                                Содержания требований
                                {!isWatchOnly &&
                                  !isDisabled &&
                                  isAddingDeletingEnabled &&
                                  (!isImpossibilityOfPrevious ||
                                    (isImpossibilityOfPrevious && !mandatoryValue?.length)) && (
                                    <span className="shared-no-wrap-text">
                                      <Button
                                        className="shared-btn-add"
                                        text="Добавить"
                                        type="button"
                                        variant="link"
                                        color="primary"
                                        id="knmRequirementsAddButton"
                                        onClick={() => handleAddMandatory(npa, structuralUnit)}
                                      />
                                    </span>
                                  )}
                                {withIconChange && (
                                  <FieldWasChangedIcon
                                    containerClassName={styles.TooltipIconRequirement}
                                    text="Добавлена новая запись"
                                  />
                                )}
                              </div>
                              {isFieldTouched && mandatoryError && (
                                <p className="shared-input-error shared-row__small-margin">{mandatoryError}</p>
                              )}
                              <FieldArray name={`${name}.mandatory`}>
                                {({ fields: mFields }) =>
                                  mFields.map((mName, mIndex) => {
                                    const mandatory = mFields.value?.[
                                      mIndex
                                    ] as IKnmErknmFormViolatedRequirementsMandatory;

                                    if (!mandatory || !mandatory.mandatory) return null;
                                    const mandatoryItemError =
                                      Array.isArray(npaErrorField?.mandatory) &&
                                      !getFieldArrayErrorMessage(npaErrorField.mandatory)
                                        ? npaErrorField?.mandatory?.[mIndex]?.id
                                        : undefined;
                                    const hasInvalidMandatoryNpa = invalidMandatoryIds?.some(
                                      id => id === mandatory?.mandatory?.id,
                                    );

                                    return (
                                      <div key={mName}>
                                        <div
                                          className={cn(styles.MandatoryBlock, {
                                            [styles.MandatoryBlockError]:
                                              hasInvalidStructureNpa ||
                                              hasInvalidMandatoryNpa ||
                                              (isLaterThanNpaValidationKnmSetting &&
                                                (mandatory.hasInvalidControlObjectFilter ||
                                                  mandatory.hasInvalidRiscIndicatorsFilter ||
                                                  mandatory.hasInvalidKno ||
                                                  mandatory.hasInvalidSupervision)),
                                          })}
                                        >
                                          <Field name={`${mName}.id`}>{() => null}</Field>
                                          <div className={styles.MandatoryBlockHeader}>
                                            Содержание требования
                                            {!isWatchOnly && !isDisabled && isAddingDeletingEnabled && (
                                              <button
                                                type="button"
                                                className={collapseStyles.DeleteButton}
                                                onClick={() => remove && remove(`${name}.mandatory`, mIndex)}
                                              >
                                                {!isImpossibilityOfPrevious && (
                                                  <span className={collapseStyles.DeleteButtonIcon} />
                                                )}
                                              </button>
                                            )}
                                          </div>
                                          <div className={styles.MandatoryBlockBody}>{mandatory.mandatory.title}</div>
                                        </div>
                                        {isFieldTouched && mandatoryItemError && (
                                          <p className="shared-input-error shared-row__small-margin">
                                            {mandatoryItemError}
                                          </p>
                                        )}
                                        {(hasInvalidStructureNpa ||
                                          hasInvalidMandatoryNpa ||
                                          (isLaterThanNpaValidationKnmSetting &&
                                            (mandatory.hasInvalidKno || mandatory.hasInvalidSupervision))) && (
                                          <p className="shared-input-error shared-row__small-margin">
                                            Требование не соответствует выбранному ВК и КНО
                                          </p>
                                        )}
                                        {isLaterThanNpaValidationKnmSetting &&
                                          mandatory.hasInvalidControlObjectFilter && (
                                            <p className="shared-input-error shared-row__small-margin">
                                              Требование не соответствует выбранным типу, виду и подвиду объекта
                                              контроля
                                            </p>
                                          )}
                                        {isLaterThanNpaValidationKnmSetting &&
                                          mandatory.hasInvalidRiscIndicatorsFilter && (
                                            <p className="shared-input-error shared-row__small-margin">
                                              Требование не соответствует выбранному индикатору риска
                                            </p>
                                          )}
                                      </div>
                                    );
                                  })
                                }
                              </FieldArray>
                            </>
                          </KnmCollapse>
                        </div>
                        {npaError && <p className="shared-input-error shared-row__small-margin">{npaError}</p>}
                        {hasInvalidExistNpa && (
                          <p className="shared-input-error shared-row__small-margin">
                            НПА не соответствует выбранному КНО и ВК
                          </p>
                        )}
                      </div>
                    );
                  })
                }
              </FieldArray>
            </div>
          )}
        </FormSpy>
        {!requirementsNpa?.length && (
          <ErknmRequirementsTable
            className={styles.Table}
            watchOnly={isWatchOnly}
            formName="requirementsErknm"
            isEnabled={isAddingDeletingEnabled}
            requirementsSelectOptions={requirementsSelectOptions}
            makeTitleSelectChangeHandler={makeTitleSelectChangeHandler}
            getItemBlockId={getRequirementBlockId}
          />
        )}
      </FieldBlock>
      {isRequirementsModalVisible && (
        <StructureRequirementsNpaModal
          onClose={() => setIsRequirementsModalVisible(false)}
          knoVersionId={knoOrganization?.value.dictVersionId || ''}
          supervisionVersionId={kindControl?.value.dictVersionId || ''}
          withRequirements={true}
          addRequirementsNpa={handleAddRequirementsNpa}
          initValues={initValues}
          objects={objects}
          riskIndicators={riskIndicators}
          addAllFields={reasonFieldValue?.type?.indicatorRiskRequired ? addAllFields : undefined}
          ignoredNpaIds={npaUsedIds}
          usedNpa={requirementsNpaValues}
        />
      )}
      {isImpossibilityOfPrevious && isRequirementDeleted && (
        <div className={styles.Yellow}>
          Для добавления сведений в раздел необходимо удалить и повторно добавить ссылку на паспорт
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmRequirementsStoreProps => ({
  formRef: getKnmFormRef(state),
  isWatchOnly: getKnmIsWatchOnly(state),
  basicRequirementsSelectOptions: getKnmErknmRequirementsSelectOptions(state),
  created: getKnmDataCreated(state),
  settings: getAdminFetchSettings(state),
  isNpaStructureChanged: getIsNpaStructureChanged(state),
  npaListNew: getNpaListNew(state),
  isRsnCngPPXXII: getIsRsnCngPPXXII(state),
  isProsecutor: getUserRoleIsProsecutor(state),
  lastChangePlanReason: getLatestKnmErknmChangePlanReason(state),
  isKnmStatusApproving: getKnmIsErknmStatusApproving(state),
  isKnmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
  isKnmStatusTypeCompleted: getKnmIsErknmStatusTypeCompleted(state),
  requirementsPlanChanges: getRequirementsPlanChanges(state),
  requirementsNew: getRequirementsNew(state),
  isPlanned: getKnmIsTypeErknmPlanned(state) || !!getKnmDataPlanId(state),
  changePlanReasonAfterConfirm: getChangePlanReasonAfterConfirmEvent(state),
  isPassportByCheckPrescriptionOldRules: getKnmErknmIsPrescriptionOldRules(state),
});

export const ErknmRequirements = connect(mapStateToProps)(ErknmRequirementsWithoutStore);
